import { httpGet, getSubscriptionOffersPath } from '@news-mono/web-common'
import { Logger } from '@west-australian-newspapers/typescript-log'
import {
    BreachWallContentType,
    SubscriptionPackageOptions,
    SubscriptionPackageTheme,
    SubscriptionPackageButtonStyle,
    RibbonOptions,
    SubscriptionPriceObject,
    SubscriptionColumnPosition,
} from '../helpers/BreachWallTypes'

interface SubscriptionOfferApiResponse {
    publicationId: string
    offerGroup: string
    variants: SubscriptionOfferVariant[]
}

interface SubscriptionOfferVariant {
    variant: string
    offers: SubscriptionOffer[]
}

interface SubscriptionOffer {
    type: 'single-subscription' | 'radio-list-subscriptions'
    heading: string
    subHeading?: string
    price: string
    priceQualifier?: string
    packageType: 'digital' | 'digitalprint'
    packageTheme?: string
    packagePath: string
    cancellationMessage: string
    googleSKU: string
    weeklyPrice: number
    rateCode: string
    buttonStyle?: string
    benefitOverride?: string[]
    displayOrder: number
    ribbonTitle?: string
    ribbonOptions?: string
    originalPrice?: string
    prices?: SubscriptionPriceObject[]
    // Optional column position for subscription grid layouts
    columnPosition?: {
        mobile: SubscriptionColumnPosition
        desktop: SubscriptionColumnPosition
    }
    // Indicates if this subscription package has the recommended banner (generally only one per subscription)
    isRecommended?: boolean
}

const validateSubscriptionOfferResponse = (
    data: any,
): SubscriptionOfferApiResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Invalid response: data is not an object')
    }

    if (!data.publicationId || typeof data.publicationId !== 'string') {
        throw new Error('Invalid response: publicationId is missing or invalid')
    }

    if (!data.offerGroup || typeof data.offerGroup !== 'string') {
        throw new Error('Invalid response: offerGroup is missing or invalid')
    }

    if (!Array.isArray(data.variants)) {
        throw new Error('Invalid response: variants is not an array')
    }

    return data as SubscriptionOfferApiResponse
}

const mapOfferToSubscriptionPackage = (
    offer: SubscriptionOffer,
): SubscriptionPackageOptions => {
    return {
        type: offer.type,
        heading: offer.heading,
        subHeading: offer.subHeading,
        price: offer.price,
        priceQualifier: offer.priceQualifier,
        packageType: offer.packageType,
        packageTheme: offer.packageTheme as SubscriptionPackageTheme,
        packagePath: offer.packagePath,
        cancellationMessage: offer.cancellationMessage,
        googleSKU: offer.googleSKU,
        weeklyPrice: offer.weeklyPrice,
        rateCode: offer.rateCode,
        buttonStyle: offer.buttonStyle as SubscriptionPackageButtonStyle,
        benefitOverride: offer.benefitOverride,
        ribbonTitle: offer.ribbonTitle,
        ribbonOptions: offer.ribbonOptions as RibbonOptions,
        originalPrice: offer.originalPrice,
        prices: offer.prices,
        columnPosition: offer.columnPosition,
        isRecommended: offer.isRecommended,
    } as SubscriptionPackageOptions
}

export const fetchSubscriptionOffers = async (
    publicationId: string,
    log: Logger,
    offerGroup = 'breach',
    variant?: string,
    config?: { subscriptionOfferApi?: string },
): Promise<Record<BreachWallContentType, SubscriptionPackageOptions[]>> => {
    try {
        const query: Record<string, string> = {}
        if (variant) {
            query.variant = variant
        }

        // For SSR: use https://{publicationId}/connect/subscription-offers (proxy handles auth)
        // For client: use relative /connect/subscription-offers (proxy handles auth)
        const isSSR = !!config?.subscriptionOfferApi

        let baseUrl = ''
        const path = getSubscriptionOffersPath.replace(/^\//, '') // 'connect/subscription-offers'

        if (isSSR) {
            // SSR: construct baseURL from publicationId to call the proxy
            baseUrl = `https://${publicationId}`
        }

        const remappedPublicationTargetId =
            publicationId.indexOf('thewest-pr') !== -1
                ? 'thewest.dev.swmdigital.io'
                : publicationId.indexOf('kalminer-pr') !== -1
                ? 'kalminer.dev.swmdigital.io'
                : publicationId.indexOf('thewest-swg') !== -1
                ? 'thewest.dev.swmdigital.io'
                : publicationId

        const urlParams = new URLSearchParams({
            publicationId: remappedPublicationTargetId, // sanitise to use dev
            offerGroup,
            ...query,
        })

        const completePath =
            baseUrl + getSubscriptionOffersPath + '?' + urlParams.toString()

        log.info(
            { completePath, publicationId, offerGroup, variant, isSSR },
            'Fetching subscription offers',
        )

        const response = await httpGet<SubscriptionOfferApiResponse>({
            log,
            validate: validateSubscriptionOfferResponse,
            baseUrl,
            path,
            query: {
                publicationId: remappedPublicationTargetId, // sanitise to use dev
                offerGroup,
                ...query,
            },
            customHeaders: false,
        })

        // Build the result map from whatever the API returns.
        // The client should not need to know all possible offer groups in advance;
        // the API drives which groups/variants are present.
        const result = {} as Record<
            BreachWallContentType,
            SubscriptionPackageOptions[]
        >

        // Map each variant's offers to the appropriate breach content type
        response.variants.forEach((variantData) => {
            const variantName = variantData.variant as BreachWallContentType

            const mapped = variantData.offers
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(mapOfferToSubscriptionPackage)

            result[variantName] = (result[variantName] || []).concat(mapped)
        })

        log.info(
            {
                offerGroup,
                variantCount: response.variants.length,
                variants: Object.keys(result),
            },
            '[RRME API] Successfully fetched offers:',
        )
        return result
    } catch (error) {
        log.error(
            { error, publicationId, offerGroup, variant },
            'Failed to fetch subscription offers. Check your SUBSCRIPTION_OFFER_API_KEY',
        )
        throw error
    }
}
